import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { useAuth } from "@/providers";
import {
  computeDefaultsOf,
  determineActionMemoryOptions,
  legalMemoryFor,
  vCpuOptions,
  type ActionRecord,
} from "@/shared/domain/actions";
import { OrganizationTier } from "@/shared/domain/orgs";
import {
  Field,
  Select,
  TextInput,
  type SelectItem,
} from "@/shared/foundation/primitives";

import styles from "./invocationSpec.module.css";

/** The longest an invocation may run, in minutes, on each tier. */
const TIMEOUT_MAX = { free: 30, premium: 720 };

const CPU_ITEMS: readonly SelectItem<string>[] = vCpuOptions.map((cpu) => ({
  value: String(cpu),
  label: `${cpu / 1024} vCPU`,
}));

export interface ComputeFieldsProps {
  /** Where the invocation block lives in the enclosing form. */
  name: string;
  /** The chosen action, whose own compute the fields override. */
  action: ActionRecord | null;
}

/**
 * The vCPU, memory, storage and timeout an invocation runs with. Each shows
 * the action's own until someone changes it, and only a changed value is
 * written, so an invocation left alone follows later changes to the action.
 * The memory on offer follows the vCPU, since the runtime validates the two
 * as a pair; changing either writes both.
 */
export function ComputeFields({ name, action }: ComputeFieldsProps) {
  const { control, setValue } = useFormContext();
  const { currentOrganization } = useAuth();
  const onFreeTier = currentOrganization?.tier === OrganizationTier.Free;
  const timeoutMax = onFreeTier ? TIMEOUT_MAX.free : TIMEOUT_MAX.premium;
  const cpu = useWatch({ control, name: `${name}.overrides.cpu` }) as
    string | undefined;
  const memory = useWatch({ control, name: `${name}.overrides.memory` }) as
    string | undefined;
  const compute = computeDefaultsOf(action);
  const shownCpu = cpu ?? compute.cpu;
  const shownMemory = memory ?? legalMemoryFor(shownCpu, compute.memory);
  const memoryItems: readonly SelectItem<string>[] =
    determineActionMemoryOptions(shownCpu).map((amount) => ({
      value: String(amount),
      label: `${amount / 1024} GiB`,
    }));

  return (
    <div className={styles.grid}>
      <Controller
        control={control}
        name={`${name}.overrides.cpu`}
        render={({ field }) => (
          <Select<string>
            items={CPU_ITEMS}
            value={shownCpu}
            onValueChange={(chosen) => {
              if (chosen === null) {
                return;
              }
              field.onChange(chosen);
              setValue(
                `${name}.overrides.memory`,
                legalMemoryFor(chosen, shownMemory),
                { shouldDirty: true },
              );
            }}
            label="vCPU"
          />
        )}
      />
      <Controller
        control={control}
        name={`${name}.overrides.memory`}
        render={({ field }) => (
          <Select<string>
            items={memoryItems}
            value={shownMemory}
            onValueChange={(chosen) => {
              if (chosen === null) {
                return;
              }
              field.onChange(chosen);
              setValue(`${name}.overrides.cpu`, shownCpu, {
                shouldDirty: true,
              });
            }}
            label="Memory"
            disabled={memoryItems.length === 0}
          />
        )}
      />
      <Controller
        control={control}
        name={`${name}.overrides.storage`}
        render={({ field, fieldState }) => (
          <Field.Root name={field.name} invalid={fieldState.invalid}>
            <Field.Label>Storage (GiB)</Field.Label>
            <Field.Control
              render={<TextInput type="number" inputMode="numeric" />}
              value={String(field.value ?? "")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const text = event.target.value;
                field.onChange(
                  text === "" ? undefined : Number.parseInt(text, 10),
                );
              }}
              onBlur={field.onBlur}
              ref={field.ref}
              placeholder={compute.storage}
            />
            <Field.Error match>{fieldState.error?.message}</Field.Error>
          </Field.Root>
        )}
      />
      <Controller
        control={control}
        name={`${name}.overrides.timeout`}
        render={({ field, fieldState }) => (
          <Field.Root name={field.name} invalid={fieldState.invalid}>
            <Field.Label>Timeout (minutes)</Field.Label>
            <Field.Control
              render={
                <TextInput type="number" inputMode="numeric" max={timeoutMax} />
              }
              value={String(field.value ?? "")}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const text = event.target.value;
                // A fraction is kept for validation to refuse, not rounded down.
                field.onChange(text === "" ? null : Number(text));
              }}
              onBlur={field.onBlur}
              placeholder={String(action?.timeout ?? timeoutMax)}
            />
            <Field.Description>
              Up to {timeoutMax} minutes on this plan.
            </Field.Description>
            <Field.Error match>{fieldState.error?.message}</Field.Error>
          </Field.Root>
        )}
      />
    </div>
  );
}
